import { MOBILE_BREAKPOINT } from '@/shared/config/layout';
import { useSyncExternalStore } from 'react';

/**
 * `true` when the viewport is narrower than {@link MOBILE_BREAKPOINT}.
 *
 * Backed by a single, module-level `matchMedia` query shared across every
 * consumer (the shell mounts this hook many times — once per sidebar button,
 * trigger, layout, etc.), so there's one media-query object rather than one per
 * component. `useSyncExternalStore` reads the query synchronously on first
 * render, so the correct value is available on the initial paint (no flash of
 * the wrong layout) with no mount-time double render. The single threshold is
 * the media query itself. SSR-safe: the server snapshot is `false`.
 */
const query = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

let mediaQuery: MediaQueryList | null = null;
function getMediaQuery(): MediaQueryList {
  mediaQuery ??= window.matchMedia(query);
  return mediaQuery;
}

function subscribe(onChange: () => void): () => void {
  const mql = getMediaQuery();
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

export function useIsMobile(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => getMediaQuery().matches,
    () => false,
  );
}
